import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Settings, Shield, Bell, Mail, Database,
  Save, RefreshCw, CheckCircle, AlertCircle, Lock,
  Clock, Globe, Zap, Server
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [settings, setSettings] = useState({
    // Security
    rate_limit_login: 5,
    rate_limit_window: 15,
    max_login_attempts: 3,
    lockout_duration: 30,
    session_timeout: 60,
    
    // Notifications
    email_notifications_enabled: true,
    slack_notifications_enabled: false,
    notify_on_new_case: true,
    notify_on_chat_message: true,
    notify_on_payout_ready: true,
    
    // Trial
    default_trial_days: 180,
    trial_reminder_days: [30, 7, 1],
    auto_delete_expired_trials: false,
    
    // Commission
    auto_upgrade_levels: true,
    payout_minimum: 50,
    payout_delay_days: 14
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadSettings();
  }, [token, navigate]);

  const loadSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(settings)
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Einstellungen gespeichert' });
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Speichern' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Verbindungsfehler' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white">System-Einstellungen</h1>
          </div>
          <span className="text-white/50 text-xs md:text-sm hidden sm:block">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {message.text}
            </span>
          </div>
        )}

        {/* Security Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Sicherheit & Rate-Limiting</h2>
              <p className="text-sm text-gray-600">Schutz vor Brute-Force Angriffen</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-1" />
                Max. Login-Versuche pro Zeitfenster
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.rate_limit_login}
                onChange={(e) => setSettings({ ...settings, rate_limit_login: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Anzahl Versuche bevor IP gesperrt wird</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Zeitfenster (Minuten)
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={settings.rate_limit_window}
                onChange={(e) => setSettings({ ...settings, rate_limit_window: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Zeitraum für Rate-Limiting</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max. fehlgeschlagene Logins (pro Konto)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.max_login_attempts}
                onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Konto-Sperrzeit (Minuten)
              </label>
              <input
                type="number"
                min="5"
                max="1440"
                value={settings.lockout_duration}
                onChange={(e) => setSettings({ ...settings, lockout_duration: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session-Timeout (Minuten)
              </label>
              <input
                type="number"
                min="15"
                max="480"
                value={settings.session_timeout}
                onChange={(e) => setSettings({ ...settings, session_timeout: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Automatische Abmeldung nach Inaktivität</p>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Benachrichtigungen</h2>
              <p className="text-sm text-gray-600">E-Mail und Push-Einstellungen</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-600" />
                <div>
                  <span className="font-medium text-gray-900">E-Mail Benachrichtigungen</span>
                  <p className="text-xs text-gray-500">Aktiviert E-Mail-Versand für alle Events</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={settings.email_notifications_enabled}
                onChange={(e) => setSettings({ ...settings, email_notifications_enabled: e.target.checked })}
                className="w-5 h-5 text-brand-green rounded"
              />
            </label>
            
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
              <div>
                <span className="font-medium text-gray-900">Neuer Fall</span>
                <p className="text-xs text-gray-500">Partner bei neuem Fall benachrichtigen</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notify_on_new_case}
                onChange={(e) => setSettings({ ...settings, notify_on_new_case: e.target.checked })}
                className="w-5 h-5 text-brand-green rounded"
              />
            </label>
            
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
              <div>
                <span className="font-medium text-gray-900">Chat-Nachrichten</span>
                <p className="text-xs text-gray-500">Bei neuen Nachrichten benachrichtigen</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notify_on_chat_message}
                onChange={(e) => setSettings({ ...settings, notify_on_chat_message: e.target.checked })}
                className="w-5 h-5 text-brand-green rounded"
              />
            </label>
            
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
              <div>
                <span className="font-medium text-gray-900">Auszahlung bereit</span>
                <p className="text-xs text-gray-500">Partner bei bereiter Provision informieren</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notify_on_payout_ready}
                onChange={(e) => setSettings({ ...settings, notify_on_payout_ready: e.target.checked })}
                className="w-5 h-5 text-brand-green rounded"
              />
            </label>
          </div>
        </div>

        {/* Trial Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Trial-Einstellungen</h2>
              <p className="text-sm text-gray-600">Testphasen-Konfiguration</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standard Trial-Dauer (Tage)
              </label>
              <input
                type="number"
                min="7"
                max="365"
                value={settings.default_trial_days}
                onChange={(e) => setSettings({ ...settings, default_trial_days: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div>
              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer h-full">
                <div>
                  <span className="font-medium text-gray-900">Auto-Löschung</span>
                  <p className="text-xs text-gray-500">Abgelaufene Trials automatisch löschen</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.auto_delete_expired_trials}
                  onChange={(e) => setSettings({ ...settings, auto_delete_expired_trials: e.target.checked })}
                  className="w-5 h-5 text-brand-green rounded"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Commission Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Provisionen & Auszahlungen</h2>
              <p className="text-sm text-gray-600">Provisions-Verwaltung</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mindest-Auszahlungsbetrag (€)
              </label>
              <input
                type="number"
                min="10"
                max="500"
                value={settings.payout_minimum}
                onChange={(e) => setSettings({ ...settings, payout_minimum: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auszahlungs-Verzögerung (Tage)
              </label>
              <input
                type="number"
                min="0"
                max="90"
                value={settings.payout_delay_days}
                onChange={(e) => setSettings({ ...settings, payout_delay_days: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Tage nach Fall-Abschluss</p>
            </div>
            
            <div className="md:col-span-2">
              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
                <div>
                  <span className="font-medium text-gray-900">Automatische Level-Erhöhung</span>
                  <p className="text-xs text-gray-500">Partner-Level basierend auf Fallanzahl automatisch erhöhen</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.auto_upgrade_levels}
                  onChange={(e) => setSettings({ ...settings, auto_upgrade_levels: e.target.checked })}
                  className="w-5 h-5 text-brand-green rounded"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            className="bg-brand-green hover:bg-brand-green-dark text-white px-8"
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Einstellungen speichern
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
